import { isEmpty } from "lodash";
import AttributeValues from "./AttributeValues";
import {
  RecordsToOverride,
  isEquivalentValue,
  getValueTypeByAttribute,
  genericOverrideURL,
  inactivateObject,
  phoneAreaString,
  phonePhExchangeString,
  phonePhNumberString,
  phoneCountryString
} from "./Utils";
import { createOverrideAuditExtension } from "../MultipleAttributeOverride/PrepareOverrides";

const PrepareOverrides = (
  attribute,
  action,
  system,
  attrCode,
  code,
  newValue,
  oldValue,
  attributeValues,
  auditDetails
) => {
  const initialPatientObject = record => {
    return Object.assign(
      {},
      {
        resourceType: record.get("resourceType"),
        id: record.get("id"),
        meta: record.get("meta")
      },
      record.get("requestTemplate")
    );
  };

  const prepareGender = (record, oldValue, newValue, requestObject) => {
    const attributeValueType = getValueTypeByAttribute("gender");

    const overrideExtension = new Object();
    overrideExtension["url"] = genericOverrideURL + "Gender/";
    overrideExtension[attributeValueType] = oldValue;
    const overrideAuditExtension = createOverrideAuditExtension(
      auditDetails,
      "gender"
    );

    requestObject["extension"] = requestObject["extension"] || new Array();
    requestObject["extension"].push(overrideExtension);
    //add audit details inside override extension
    overrideAuditExtension.forEach(ext => {
      requestObject["extension"].push(ext);
    });
    requestObject["gender"] = newValue;

    return requestObject;
  };

  const prepareBirthDate = (record, oldValue, newValue, requestObject) => {
    const attributeValueType = getValueTypeByAttribute("birthDate");

    const overrideExtension = new Object();
    overrideExtension["url"] = genericOverrideURL + "BirthDate/";
    overrideExtension[attributeValueType] = oldValue;
    const overrideAuditExtension = createOverrideAuditExtension(
      auditDetails,
      "birthDate"
    );

    requestObject["extension"] = requestObject["extension"] || new Array();
    requestObject["extension"].push(overrideExtension);
    //add audit details inside override extension
    overrideAuditExtension.forEach(ext => {
      requestObject["extension"].push(ext);
    });
    // Set the birthDate or gender values.
    requestObject["birthDate"] = newValue;

    return requestObject;
  };

  const prepareAddress = (recordMap, action, newValuesMap, patientObject) => {
    const attributeValueType = getValueTypeByAttribute("address");
    const addressElements = [
      "use",
      "line",
      "city",
      "district",
      "state",
      "country",
      "postalCode",
      "period"
    ];

    const overrideObject = new Object();
    overrideObject["extension"] = new Array();

    const previousValues = new Object();
    previousValues["url"] = genericOverrideURL;
    previousValues[attributeValueType] = new Object();

    //create audit details
    const overrideAuditArray = createOverrideAuditExtension(auditDetails);

    if (recordMap.has("extensionObject")) {
      previousValues[attributeValueType]["extension"] = recordMap.get(
        "extensionObject"
      );
      overrideObject["extension"].push(...recordMap.get("extensionObject"));
    }

    addressElements.forEach(element => {
      if (recordMap.has(element)) {
        previousValues[attributeValueType][element] = recordMap.get(element);
      }
    });

    overrideObject["extension"].push(previousValues);
    //add audit details inside extension
    overrideAuditArray.forEach(ext => {
      overrideObject["extension"].push(ext);
    });

    switch (action) {
      case "inactive":
        Object.assign(
          overrideObject,
          inactivateObject(previousValues[attributeValueType])
        );
        break;

      case "replace":
        if (recordMap.has("use")) {
          overrideObject["use"] = recordMap.get("use");
        }

        addressElements
          .filter(e => e !== "use" && e !== "period")
          .forEach(e => {
            if (newValuesMap.has(e)) {
              overrideObject[e] = newValuesMap.get(e);
            }
          });

        if (recordMap.has("period")) {
          overrideObject["period"] = recordMap.get("period");
        }
        break;

      case "invalid":
        overrideObject["extension"] = overrideObject["extension"].filter(ext =>
          [
            genericOverrideURL,
            ...overrideAuditArray.map(audit => audit.url)
          ].includes(ext.url)
        );
        break;
    }

    patientObject["address"] = patientObject["address"] || new Array();
    patientObject["address"].push(overrideObject);

    return patientObject;
  };

  const prepareName = (recordMap, action, newValuesMap, patientObject) => {
    const attributeValueType = getValueTypeByAttribute("name");
    const nameElements = ["use", "given", "family", "period"];

    const overrideObject = new Object();
    overrideObject["extension"] = new Array();

    const previousValues = new Object();
    previousValues["url"] = genericOverrideURL;
    previousValues[attributeValueType] = new Object();
    //create audit details
    const overrideAuditArray = createOverrideAuditExtension(auditDetails);

    if (recordMap.has("extensionObject")) {
      previousValues[attributeValueType]["extension"] = recordMap.get(
        "extensionObject"
      );
      overrideObject["extension"].push(...recordMap.get("extensionObject"));
    }

    nameElements.forEach(element => {
      if (recordMap.has(element)) {
        previousValues[attributeValueType][element] = recordMap.get(element);
      }
    });

    overrideObject["extension"].push(previousValues);
    //add audit details inside extension
    overrideAuditArray.forEach(ext => {
      overrideObject["extension"].push(ext);
    });

    switch (action) {
      case "inactive":
        Object.assign(
          overrideObject,
          inactivateObject(previousValues[attributeValueType])
        );
        break;

      case "replace":
        if (recordMap.has("use")) {
          overrideObject["use"] = recordMap.get("use");
        }

        nameElements
          .filter(e => e !== "use" && e !== "period")
          .forEach(e => {
            if (newValuesMap.has(e)) {
              overrideObject[e] = newValuesMap.get(e);
            }
          });

        if (recordMap.has("period")) {
          overrideObject["period"] = recordMap.get("period");
        }
        break;

      case "invalid":
        overrideObject["extension"] = overrideObject["extension"].filter(ext =>
          [
            genericOverrideURL,
            ...overrideAuditArray.map(audit => audit.url)
          ].includes(ext.url)
        );
        break;
    }

    patientObject["name"] = patientObject["name"] || new Array();
    patientObject["name"].push(overrideObject);

    return patientObject;
  };

  const prepareIdentifier = (
    recordMap,
    action,
    newValuesMap,
    patientObject
  ) => {
    const attributeValueType = getValueTypeByAttribute("identifier");
    const identifierElements = ["use", "type", "system", "value", "period"];

    const overrideObject = new Object();
    overrideObject["extension"] = new Array();

    const previousValues = new Object();
    previousValues["url"] = genericOverrideURL;
    previousValues[attributeValueType] = new Object();
    //create audit details
    const overrideAuditArray = createOverrideAuditExtension(auditDetails);

    if (recordMap.has("extensionObject")) {
      previousValues[attributeValueType]["extension"] = recordMap.get(
        "extensionObject"
      );
      overrideObject["extension"].push(...recordMap.get("extensionObject"));
    }

    identifierElements.forEach(element => {
      if (recordMap.has(element)) {
        previousValues[attributeValueType][element] = recordMap.get(element);
      }
    });

    overrideObject["extension"].push(previousValues);
    //add audit details inside extension
    overrideAuditArray.forEach(ext => {
      overrideObject["extension"].push(ext);
    });

    switch (action) {
      case "inactive":
        Object.assign(
          overrideObject,
          inactivateObject(previousValues[attributeValueType])
        );
        break;

      case "replace":
        if (recordMap.has("use")) {
          overrideObject["use"] = recordMap.get("use");
        }
        if (recordMap.has("type")) {
          overrideObject["type"] = recordMap.get("type");
        }
        if (recordMap.has("system")) {
          overrideObject["system"] = recordMap.get("system");
        }
        if (newValuesMap.has("value")) {
          overrideObject["value"] = newValuesMap.get("value");
        }
        if (recordMap.has("period")) {
          overrideObject["period"] = recordMap.get("period");
        }
        break;

      case "invalid":
        overrideObject["extension"] = overrideObject["extension"].filter(ext =>
          [
            genericOverrideURL,
            ...overrideAuditArray.map(audit => audit.url)
          ].includes(ext.url)
        );
        break;
    }

    patientObject["identifier"] = patientObject["identifier"] || new Array();
    patientObject["identifier"].push(overrideObject);

    return patientObject;
  };

  const preparePhone = (recordMap, action, newValuesMap, patientObject) => {
    const attributeValueType = getValueTypeByAttribute("phone");

    const overrideObject = new Object();
    overrideObject["extension"] = new Array();

    const previousValues = new Object();
    previousValues["url"] = genericOverrideURL;
    previousValues[attributeValueType] = new Object();
    //create audit details
    const overrideAuditArray = createOverrideAuditExtension(auditDetails);

    if (recordMap.has("extensionObject")) {
      previousValues[attributeValueType]["extension"] = recordMap.get(
        "extensionObject"
      );
      const phoneExtObject = recordMap
        .get("extensionObject")
        .filter(
          e =>
            e.hasOwnProperty("url") &&
            ![
              phoneAreaString,
              phonePhExchangeString,
              phonePhNumberString,
              phoneCountryString
            ].includes(e.url)
        );
      overrideObject["extension"].push(...phoneExtObject);
    }

    if (recordMap.has("use")) {
      previousValues[attributeValueType]["use"] = recordMap.get("use");
    }
    previousValues[attributeValueType]["system"] = "phone";
    if (recordMap.has("phone")) {
      previousValues[attributeValueType]["value"] = recordMap.get("phone");
    }
    if (recordMap.has("period")) {
      previousValues[attributeValueType]["period"] = recordMap.get("period");
    }

    overrideObject["extension"].push(previousValues);
    //add audit details inside extension
    overrideAuditArray.forEach(ext => {
      overrideObject["extension"].push(ext);
    });

    switch (action) {
      case "inactive":
        Object.assign(
          overrideObject,
          inactivateObject(previousValues[attributeValueType])
        );
        const phAreaObject = new Object();
        phAreaObject["url"] = phoneAreaString;
        phAreaObject["valueString"] = recordMap.has("phone")
          ? recordMap.get("phone").substring(0, 3)
          : "";
        overrideObject["extension"].push(phAreaObject);

        const phExchangeObject = new Object();
        phExchangeObject["url"] = phonePhExchangeString;
        phExchangeObject["valueString"] = recordMap.has("phone")
          ? recordMap.get("phone").substring(3, 6)
          : "";
        overrideObject["extension"].push(phExchangeObject);

        const phNumberObject = new Object();
        phNumberObject["url"] = phonePhNumberString;
        phNumberObject["valueString"] = recordMap.has("phone")
          ? recordMap.get("phone").substring(6, 10)
          : "";
        overrideObject["extension"].push(phNumberObject);

        const phCountryObject = new Object();
        phCountryObject["url"] = phoneCountryString;
        phCountryObject["valueString"] = "1";
        overrideObject["extension"].push(phCountryObject);
        break;

      case "replace":
        const phAreaObjectCorrectValue = new Object();
        phAreaObjectCorrectValue["url"] = phoneAreaString;
        phAreaObjectCorrectValue["valueString"] = newValuesMap.has("phone")
          ? newValuesMap.get("phone").substring(0, 3)
          : "";
        overrideObject["extension"].push(phAreaObjectCorrectValue);

        const phExchangeObjectCorrectValue = new Object();
        phExchangeObjectCorrectValue["url"] = phonePhExchangeString;
        phExchangeObjectCorrectValue["valueString"] = newValuesMap.has("phone")
          ? newValuesMap.get("phone").substring(3, 6)
          : "";
        overrideObject["extension"].push(phExchangeObjectCorrectValue);

        const phNumberObjectCorrectValue = new Object();
        phNumberObjectCorrectValue["url"] = phonePhNumberString;
        phNumberObjectCorrectValue["valueString"] = newValuesMap.has("phone")
          ? newValuesMap.get("phone").substring(6, 10)
          : "";
        overrideObject["extension"].push(phNumberObjectCorrectValue);

        const phCountryObjectCorrectValue = new Object();
        phCountryObjectCorrectValue["url"] = phoneCountryString;
        phCountryObjectCorrectValue["valueString"] = "1";
        overrideObject["extension"].push(phCountryObjectCorrectValue);

        if (recordMap.has("use")) {
          overrideObject["use"] = recordMap.get("use");
        }
        overrideObject["system"] = "phone";
        if (newValuesMap.has("phone")) {
          overrideObject["value"] = newValuesMap.get("phone");
        }
        if (recordMap.has("period")) {
          overrideObject["period"] = recordMap.get("period");
        }
        break;

      case "invalid":
        overrideObject["extension"] = overrideObject["extension"].filter(ext =>
          [
            genericOverrideURL,
            ...overrideAuditArray.map(audit => audit.url)
          ].includes(ext.url)
        );
        break;
    }

    patientObject["telecom"] = patientObject["telecom"] || new Array();
    patientObject["telecom"].push(overrideObject);

    return patientObject;
  };

  const prepareEmail = (recordMap, action, newValuesMap, patientObject) => {
    const attributeValueType = getValueTypeByAttribute("email");

    const overrideObject = new Object();
    overrideObject["extension"] = new Array();

    const previousValues = new Object();
    previousValues["url"] = genericOverrideURL;
    previousValues[attributeValueType] = new Object();
    //create audit details
    const overrideAuditArray = createOverrideAuditExtension(auditDetails);

    if (recordMap.has("extensionObject")) {
      previousValues[attributeValueType]["extension"] = recordMap.get(
        "extensionObject"
      );
      overrideObject["extension"].push(...recordMap.get("extensionObject"));
    }

    previousValues[attributeValueType]["system"] = "email";
    if (recordMap.has("email")) {
      previousValues[attributeValueType]["value"] = recordMap.get("email");
    }
    if (recordMap.has("use")) {
      previousValues[attributeValueType]["use"] = recordMap.get("use");
    }
    if (recordMap.has("period")) {
      previousValues[attributeValueType]["period"] = recordMap.get("period");
    }

    overrideObject["extension"].push(previousValues);
    //add audit details inside extension
    overrideAuditArray.forEach(ext => {
      overrideObject["extension"].push(ext);
    });

    switch (action) {
      case "inactive":
        Object.assign(
          overrideObject,
          inactivateObject(previousValues[attributeValueType])
        );
        break;

      case "replace":
        overrideObject["system"] = "email";
        if (newValuesMap.has("email")) {
          overrideObject["value"] = newValuesMap.get("email");
        }
        if (recordMap.has("use")) {
          overrideObject["use"] = recordMap.get("use");
        }
        if (recordMap.has("period")) {
          overrideObject["period"] = recordMap.get("period");
        }
        break;

      case "invalid":
        overrideObject["extension"] = overrideObject["extension"].filter(ext =>
          [
            genericOverrideURL,
            ...overrideAuditArray.map(audit => audit.url)
          ].includes(ext.url)
        );
        break;
    }

    patientObject["telecom"] = patientObject["telecom"] || new Array();
    patientObject["telecom"].push(overrideObject);

    return patientObject;
  };

  const newValuesMap = attributeValues
    .flat()
    .find(v => isEquivalentValue(v.get("displayValue"), newValue, attribute));

  const recordMapsToOverride = RecordsToOverride(
    attributeValues,
    attribute,
    system,
    attrCode,
    action,
    oldValue,
    code
  );

  const finalPatientObjectList = recordMapsToOverride.map(recordMap => {
    const patientObject = initialPatientObject(recordMap);
    patientObject.meta.lastUpdated = "";

    switch (attribute) {
      case "birthDate":
        return prepareBirthDate(recordMap, oldValue, newValue, patientObject);
        break;

      case "gender":
        return prepareGender(recordMap, oldValue, newValue, patientObject);
        break;

      case "address":
        return prepareAddress(recordMap, action, newValuesMap, patientObject);
        break;

      case "name":
        return prepareName(recordMap, action, newValuesMap, patientObject);
        break;

      case "identifier":
        return prepareIdentifier(
          recordMap,
          action,
          newValuesMap,
          patientObject
        );
        break;

      case "phone":
        return preparePhone(recordMap, action, newValuesMap, patientObject);
        break;

      case "email":
        return prepareEmail(recordMap, action, newValuesMap, patientObject);
        break;
    }
  });

  return finalPatientObjectList;
};

export default PrepareOverrides;
